import { AlertTriangle } from "lucide-react";
import { TV_BLUE } from "@/components/broadcast/TvFrame";
import { FLT_CAT } from "@/components/broadcast/TvMap";
import { BrightStage, Pill } from "@/components/broadcast/shared";

function catChip(cat) {
  const c = FLT_CAT[cat];
  return (
    <span className="inline-flex items-center rounded-[4px] px-3 py-1 font-oswald text-[22px] font-black uppercase tracking-wide text-white" style={{ background: c ? c.color : "#6b7280" }}>
      {c ? c.label : (cat || "—")}
    </span>
  );
}
function AviationBoardSlide({ aviation }) {
  const airports = (aviation?.airports || []).filter((a) => a.cat);
  if (!airports.length) return null;
  const order = { VFR: 0, MVFR: 1, IFR: 2, LIFR: 3 };
  const sorted = [...airports].sort((a, b) => (order[b.cat] ?? -1) - (order[a.cat] ?? -1) || a.name.localeCompare(b.name));
  const fmtCeil = (ft) => (ft == null ? "Unlimited" : `${ft.toLocaleString()} ft`);
  const fmtVis = (mi) => (mi == null ? "—" : `${mi >= 10 ? "10+" : mi} mi`);
  const fmtWind = (a) => (a.wind_mph == null ? "—" : a.wind_mph < 3 ? "Calm" : `${a.wind_mph}${a.gust_mph ? `G${a.gust_mph}` : ""} mph`);
  return (
    <BrightStage testid="aviation-board-slide" className="p-[40px]">
      <div className="mb-3 flex items-center justify-end gap-4">
        {["VFR", "MVFR", "IFR", "LIFR"].map((k) => (
          <span key={k} className="flex items-center gap-2 font-oswald text-[22px] font-bold uppercase tracking-wide text-[#5b6675]">
            <span className="h-4 w-4 rounded-full" style={{ background: FLT_CAT[k].color }} />{k}
          </span>
        ))}
      </div>
      <div className="overflow-hidden rounded-[8px] bg-white shadow-[0_12px_30px_rgba(10,15,28,0.12)]" data-testid="aviation-board-table">
        <div className="grid grid-cols-[1.6fr_1fr_1.1fr_1fr_1.1fr_0.9fr] items-center bg-[#0a0f1c] px-6 py-2 font-oswald text-[22px] font-bold uppercase tracking-[0.2em] text-white/90">
          <div>Airport</div><div>Status</div><div>Ceiling</div><div>Visibility</div><div>Wind</div><div>Temp</div>
        </div>
        {sorted.map((a, i) => (
          <div key={a.id} className={`grid grid-cols-[1.6fr_1fr_1.1fr_1fr_1.1fr_0.9fr] items-center px-6 py-[9px] ${i % 2 ? "bg-[#eef1f5]" : "bg-white"}`} data-testid={`aviation-row-${a.id}`}>
            <div className="flex items-baseline gap-3">
              <span className="font-oswald text-[28px] font-black text-[#0a0f1c]">{a.name}</span>
              <span className="font-oswald text-[21px] font-bold uppercase tracking-wide text-[#8a95a3]">{a.id}</span>
            </div>
            <div>{catChip(a.cat)}</div>
            <div className="font-oswald text-[24px] font-bold text-[#0a0f1c]">{fmtCeil(a.ceiling_ft)}</div>
            <div className="font-oswald text-[24px] font-bold text-[#0a0f1c]">{fmtVis(a.visib_mi)}</div>
            <div className="font-oswald text-[24px] font-bold text-[#0a0f1c]">{fmtWind(a)}</div>
            <div className="font-oswald text-[24px] font-bold" style={{ color: TV_BLUE }}>{a.temp_c == null ? "—" : `${Math.round(a.temp_c * 9 / 5 + 32)}°`}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 font-oswald text-[19px] uppercase tracking-widest text-[#5b6675]">NOAA Aviation Weather Center · live METAR observations</div>
    </BrightStage>
  );
}
function AviationHazardsSlide({ aviation }) {
  const hazards = aviation?.hazards || [];
  if (!hazards.length) return null;
  const fmtAlt = (h) => {
    if (h.alt_hi && h.alt_low) return `${(h.alt_low / 100).toFixed(0)}–${(h.alt_hi / 100).toFixed(0)} hundreds ft`;
    if (h.alt_hi) return `up to FL${(h.alt_hi / 100).toFixed(0)}`;
    return "All levels";
  };
  const fmtTime = (ts) => (ts ? new Date(ts * 1000).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" }) + " ET" : "—");
  return (
    <BrightStage testid="aviation-hazards-slide" className="p-[48px]">
      <div className="mb-5 flex items-center justify-between">
        <Pill color="#d90429"><AlertTriangle className="h-5 w-5" /> Aviation Hazards</Pill>
        <div className="font-oswald text-[20px] font-bold uppercase tracking-[0.3em] text-[#5b6675]">SIGMET / AIRMET · Georgia airspace</div>
      </div>
      <div className="grid h-[calc(100%-80px)] auto-rows-min grid-cols-2 gap-4 overflow-hidden" data-testid="aviation-hazards-list">
        {hazards.slice(0, 4).map((h) => {
          const isSig = h.kind === "SIGMET";
          return (
            <div key={h.id} className="flex flex-col gap-2 rounded-[8px] bg-white p-6 shadow-[0_12px_30px_rgba(10,15,28,0.12)]" style={{ borderLeft: `10px solid ${isSig ? "#d90429" : "#f6a21c"}` }} data-testid={`aviation-hazard-${h.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-oswald text-[34px] font-black uppercase tracking-wide text-[#0a0f1c]">{h.label}</span>
                <span className="rounded-[4px] px-3 py-1 font-oswald text-[20px] font-black uppercase tracking-wider text-white" style={{ background: isSig ? "#d90429" : "#f6a21c" }}>{h.kind}</span>
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-1 font-oswald text-[22px] font-bold uppercase tracking-wide text-[#5b6675]">
                <span>Levels: <span className="text-[#0a0f1c]">{fmtAlt(h)}</span></span>
                <span>Until: <span className="text-[#0a0f1c]">{fmtTime(h.valid_to)}</span></span>
              </div>
              {h.raw && <div className="mt-1 line-clamp-3 font-ibm text-[20px] leading-snug text-[#3a434f]">{h.raw}</div>}
            </div>
          );
        })}
      </div>
      <div className="mt-2 font-oswald text-[19px] uppercase tracking-widest text-[#5b6675]">NOAA Aviation Weather Center · active in-flight advisories</div>
    </BrightStage>
  );
}

export { AviationBoardSlide, AviationHazardsSlide };
